import React from 'react';
import { Box, Flex, Text, Heading, Icon, Spinner, Tooltip } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Clock, Info } from 'phosphor-react';
import { useDatasEmpresa, IPermanenciaMedia } from '../../../hooks/useDatasEmpresa';
import { IDataNoPagination } from '../../../types/shared';

export function PermanenciaMediaCard() {
  const { getPermanenciaMedia } = useDatasEmpresa();

  const queryKey = ['permanenciaMedia'];

  const { data, isFetching, isError } = useQuery<IDataNoPagination<IPermanenciaMedia>>({
    queryKey,
    queryFn: getPermanenciaMedia,
  });

  const permanenciaData = data?.data;
  const hasData =
    permanenciaData?.permanencia_media_dias !== undefined &&
    permanenciaData?.permanencia_media_dias !== null &&
    permanenciaData?.permanencia_media_dias > 0;

  const formatDays = (days: number) => {
    if (days >= 365) {
      const years = Math.floor(days / 365);
      const remainingDays = Math.round(days % 365);
      if (remainingDays > 0) {
        return `${years} ano${years > 1 ? 's' : ''} e ${remainingDays} dias`;
      }
      return `${years} ano${years > 1 ? 's' : ''}`;
    }
    return `${Math.round(days)} dias`;
  };

  return (
    <Box bg="white" p={{ base: 3, md: 6 }} borderRadius="xl" boxShadow="lg" h="100%">
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size={{ base: 'sm', md: 'md' }} color="gray.700">
          <Flex align="center" gap={2}>
            <Icon as={Clock} boxSize={{ base: 4, md: 5 }} />
            Permanência Média
            <Tooltip
              label="Tempo médio de permanência das empresas que saíram, calculado desde a data de entrada até a data de saída."
              placement="top"
              hasArrow
              bg="gray.700"
              color="white"
              fontSize="xs"
              px={3}
              py={2}
              borderRadius="md"
            >
              <Box as="span" cursor="help">
                <Icon as={Info} boxSize={4} color="gray.400" />
              </Box>
            </Tooltip>
          </Flex>
        </Heading>
      </Flex>

      <Flex
        h="calc(100% - 50px)"
        p={{ base: 4, md: 6 }}
        bgGradient="linear(to-r, cyan.400, cyan.600)"
        borderRadius="lg"
        color="white"
        textAlign="center"
        align="center"
        justify="center"
        direction="column"
      >
        {isFetching && <Spinner size="lg" color="white" />}
        {!isFetching && isError && <Text>Erro ao carregar dados</Text>}
        {!isFetching && !isError && !hasData && (
          <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight="medium">
            Sem dados disponíveis
          </Text>
        )}
        {!isFetching && !isError && hasData && (
          <>
            <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight="medium" mb={2}>
              Tempo Médio de Permanência
            </Text>
            <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold">
              {formatDays(permanenciaData.permanencia_media_dias)}
            </Text>
            <Text fontSize={{ base: 'xs', md: 'sm' }} opacity={0.9} mt={1}>
              ({permanenciaData.permanencia_media_dias.toFixed(1)} dias)
            </Text>
          </>
        )}
      </Flex>
    </Box>
  );
}
